import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { FevDeCartera, RenglonDelDocumento } from '@/lib/supabase/tipos-bd'
import { HojaCotizacion, type DatosHoja } from '@/modules/cotizaciones/hoja/HojaCotizacion'
import { Cargando } from '@/shared/ui/Estados/Cargando'

/**
 * EL DETALLE DE UNA FEV, EN SU HOJA.
 *
 * Johan: «que en esa consulta histórica se me muestren dentro de la ODS
 * todas las FEV… y que yo pueda entrar a detalle de cada una». No se dibuja
 * un documento nuevo: se usa la MISMA hoja de las cotizaciones
 * (HojaCotizacion, con su «Imprimir o guardar en PDF»), con el título de
 * factura. Los renglones salen de fn_renglones_del_documento('factura'), la
 * misma función del detalle de la orden, que lee wo_factura_renglones; la
 * cabecera, de la factura y del cliente en el espejo de World Office.
 */
type CabeceraFactura = {
  prefijo: string | null
  numero: string | number | null
  fecha: string | null
  id_tercero: number | null
  cliente_nombre: string | null
  cliente_identificacion: string | null
  vendedor_nombre: string | null
  total: number | null
  nota: string | null
}

type ClienteEspejo = {
  direccion: string | null
  telefono: string | null
  email: string | null
  ciudad: string | null
}

async function armarFactura(fev: FevDeCartera, ordenNumero: string): Promise<DatosHoja> {
  const [{ data: f, error: e1 }, { data: renglones, error: e2 }] = await Promise.all([
    supabase
      .from('wo_facturas' as never)
      .select(
        'prefijo, numero, fecha, id_tercero, cliente_nombre, cliente_identificacion, vendedor_nombre, total, nota',
      )
      .eq('id_asiento', fev.wo_id_asiento)
      .maybeSingle(),
    supabase.rpc('fn_renglones_del_documento', {
      p_clase: 'factura',
      p_referencia: String(fev.wo_id_asiento),
    }),
  ])
  if (e1) throw new Error(mensajeDeError(e1))
  if (e2) throw new Error(mensajeDeError(e2))

  const cab = (f ?? null) as CabeceraFactura | null

  let cli: ClienteEspejo | null = null
  if (cab?.id_tercero) {
    const { data } = await supabase
      .from('wo_clientes' as never)
      .select('direccion, telefono, email, ciudad')
      .eq('id_tercero', cab.id_tercero)
      .maybeSingle()
    cli = (data ?? null) as ClienteEspejo | null
  }

  const filas = ((renglones ?? []) as unknown as RenglonDelDocumento[]).map((r) => {
    const cantidad = Number(r.cantidad ?? 0)
    const precio = Number(r.precio_unitario ?? 0)
    const tasa = r.iva === null ? null : Number(r.iva)
    const base = r.subtotal === null ? cantidad * precio : Number(r.subtotal)
    return {
      codigo: null,
      descripcion: r.descripcion,
      cantidad,
      unidad: r.unidad,
      precioUnitario: precio,
      ivaTasa: tasa,
      // Por unidad, como en el documento de World Office (ver armarHoja).
      ivaValor: tasa === null ? 0 : Math.round(precio * tasa),
      total: Math.floor(base),
      base,
      ivaTotal: tasa === null ? 0 : base * tasa,
    }
  })

  const subtotal = Math.floor(filas.reduce((s, r) => s + r.base, 0))
  const iva = Math.round(filas.reduce((s, r) => s + r.ivaTotal, 0))
  const total = cab?.total !== null && cab?.total !== undefined ? Number(cab.total) : subtotal + iva

  return {
    titulo: 'FACTURA ELECTRÓNICA DE VENTA',
    // En el espejo el número llega como número; la hoja lo trata como texto.
    numero: cab?.numero !== null && cab?.numero !== undefined ? String(cab.numero) : fev.numero,
    prefijo: cab?.prefijo ?? null,
    fecha: cab?.fecha ?? fev.fecha,
    cliente: cab?.cliente_nombre ?? '—',
    clienteDocumento: cab?.cliente_identificacion ?? null,
    clienteDireccion: cli?.direccion ?? null,
    clienteTelefono: cli?.telefono ?? null,
    clienteCiudad: cli?.ciudad ?? null,
    clienteCorreo: cli?.email ?? null,
    vendedor: cab?.vendedor_nombre ?? null,
    concepto: [
      `Orden de servicio ${ordenNumero}`,
      fev.de_la_cotizacion ? `de la cotización ${fev.de_la_cotizacion}` : null,
      fev.cufe ? `CUFE ${fev.cufe}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    lineas: filas.map(({ base: _b, ivaTotal: _i, ...l }) => l),
    subtotal,
    iva: Math.max(total - subtotal, iva),
    total,
  }
}

export function HojaDeLaFactura({
  fev,
  ordenNumero,
  onCerrar,
}: {
  fev: FevDeCartera
  ordenNumero: string
  onCerrar: () => void
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hoja-de-la-factura', fev.wo_id_asiento],
    queryFn: () => armarFactura(fev, ordenNumero),
    staleTime: 5 * 60_000,
  })

  if (error) {
    return (
      <HojaCotizacion
        datos={{
          titulo: 'FACTURA ELECTRÓNICA DE VENTA',
          numero: fev.numero,
          prefijo: null,
          fecha: fev.fecha,
          cliente: (error as Error).message,
          clienteDocumento: null,
          clienteDireccion: null,
          clienteTelefono: null,
          clienteCiudad: null,
          clienteCorreo: null,
          vendedor: null,
          concepto: null,
          lineas: [],
          subtotal: 0,
          iva: 0,
          total: Number(fev.total),
        }}
        onCerrar={onCerrar}
      />
    )
  }
  if (isLoading || !data) return <Cargando mensaje="GENERANDO FACTURA VENTA..." />
  return <HojaCotizacion datos={data} onCerrar={onCerrar} />
}
